import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Bot, User, Sparkles, ArrowLeft, Loader2 } from "lucide-react";
import axios from "axios";

const AIAssistantPage = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: userMessage, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      toast.error("Failed to get response. Please try again.");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    "What career path should I choose?",
    "How can I improve my resume?",
    "What skills are in demand in 2025?",
    "How do I prepare for tech interviews?",
  ];

  return (
    <Layout>
      <div className="min-h-[calc(100vh-80px)] flex flex-col" data-testid="ai-assistant-page">
        {/* Header */}
        <div className="bg-slate-900 text-white py-6">
          <div className="container-padding">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-slate-900" />
                </div>
                <div>
                  <h1 className="font-heading text-xl font-medium">AI Career Assistant</h1>
                  <p className="text-sm text-slate-400">Powered by GPT</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto bg-slate-50" data-testid="chat-area">
          <div className="container-padding py-8">
            {messages.length === 0 ? (
              <div className="max-w-2xl mx-auto text-center">
                <div className="w-20 h-20 bg-lime-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Bot className="w-10 h-10 text-lime-600" />
                </div>
                <h2 className="font-heading text-2xl font-light text-slate-900 mb-4">
                  How can I help you today?
                </h2>
                <p className="text-slate-600 mb-8">
                  I'm your AI career counselor. Ask me anything about careers, courses, 
                  interviews, or professional development.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setInput(question)}
                      className="p-4 bg-white border border-slate-200 rounded-xl text-left hover:border-lime-500 hover:shadow-md transition-all"
                      data-testid={`suggested-question-${index}`}
                    >
                      <p className="text-slate-900">{question}</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-6">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-4 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                    data-testid={`message-${index}`}
                  >
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        message.role === "user"
                          ? "bg-slate-900 text-white"
                          : "bg-lime-100 text-lime-600"
                      }`}
                    >
                      {message.role === "user" ? (
                        <User className="w-5 h-5" />
                      ) : (
                        <Bot className="w-5 h-5" />
                      )}
                    </div>
                    <div
                      className={`max-w-[80%] px-6 py-4 rounded-2xl ${
                        message.role === "user"
                          ? "bg-slate-900 text-white rounded-br-md"
                          : "bg-white border border-slate-100 text-slate-900 rounded-bl-md"
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-lime-600" />
                    </div>
                    <div className="bg-white border border-slate-100 px-6 py-4 rounded-2xl rounded-bl-md">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Input Area */}
        <div className="bg-white border-t border-slate-100 py-4">
          <div className="container-padding">
            <form onSubmit={handleSend} className="max-w-3xl mx-auto flex gap-4" data-testid="chat-form">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything about your career..."
                className="flex-1 py-6 rounded-full"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8"
                data-testid="send-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </form>
            <p className="text-center text-xs text-slate-400 mt-3">
              AI responses are for guidance only. For personalized advice, book a session with our counselors.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AIAssistantPage;
